import React, { ReactElement, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import fileDownload from 'js-file-download';
import { makeStyles } from '@mui/styles';
import { colors, theme } from '../../theme';
import { CustomButton } from '../shared/button/button';
import { AddCertificateDocument } from './addCertificateDocument';
import { CertificateDocumentsTable, DocumentRowType } from './certificateDocumentsTable';
import { ItemType, PostResponse } from '../../types/common.types';
import { msalInstance } from '../../config/msalConfig';
import { useAsyncOperation } from '../../utils/use-async-operation';
import {
	downloadCUFile,
	deleteCertificateAdditionalDocument,
	getCUBlobSasToken,
	getCertificateAdditionalDocuments,
	saveCertificateAdditionalDocument,
} from '../../services/cu-service';
import { CUCertificateAdditionalDocumentType } from '../../types/cu.types';
import { Loader } from '../shared/loader/loader';
import { CustomAlert } from '../shared/alert/alert';
import { CustomDialog } from '../shared/dialog/dialog';

const color = colors();
const themes = theme().typography;

export const CertificateAdditionalDocuments = (): ReactElement => {
	const classes = useStyles();
	const isAdmin: boolean = localStorage.getItem('roles')?.includes('TT.BrandSCAdmin') || false;
	const userAccount = msalInstance.getActiveAccount();
	const loggedInUser = userAccount ? `${userAccount.name?.split('(')[0]}| ${userAccount.username}` : '';
	const inputRef = useRef<HTMLInputElement>(null);
	const documentTypeOptions: ItemType[] = [
		{ key: 1, value: 'Certificate' },
		{ key: 2, value: 'Agreement' },
		{ key: 3, value: 'Other' },
	];

	const [openCreate, setOpenCreate] = useState<boolean>(false);
	const [documentType, setDocumentType] = useState<string>('');
	const [otherDocumentType, setOtherDocumentType] = useState<string>('');
	const [selectedFile, setSelectedFile] = useState<File>();
	const [error, setError] = useState<string>('');
	const [rows, setRows] = useState<DocumentRowType[]>([]);
	const [openError, setOpenError] = useState<boolean>(false);
	const [openSuccess, setOpenSuccess] = useState<boolean>(false);
	const [actionBusy, setActionBusy] = useState<boolean>(false);
	const [actionMessage, setActionMessage] = useState<string>('Loading...');
	const [successMessage, setSuccessMessage] = useState<string>('');
	const initialLoadTriggered = useRef<boolean>(false);
	const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
	const [selectedRow, setSelectedRow] = useState<DocumentRowType>();

	const [getDocuments, getDocumentsBusy, documentData, documentError] = useAsyncOperation<
		CUCertificateAdditionalDocumentType[]
	>(getCertificateAdditionalDocuments);
	const [saveDocument, saveBusy, saveData, saveError] = useAsyncOperation<PostResponse>(
		saveCertificateAdditionalDocument
	);
	const [deleteDocument, deleteBusy, deleteData, deleteError] = useAsyncOperation<PostResponse>(
		deleteCertificateAdditionalDocument
	);

	useEffect(() => {
		if (initialLoadTriggered.current) return;
		initialLoadTriggered.current = true;
		getDocuments();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (documentData) {
			setRows(
				documentData.map((doc) => {
					const uploadDate = doc.updatedDate || doc.createdDate;
					const resolvedBlobPath = doc.documentURL || '';
					const updatedByName = (doc.updatedBy || '').split('|')[0].trim();
					return {
						id: doc.id || `${doc.documentType || ''}-${doc.documentName || ''}`,
						documentType: doc.documentType || '',
						documentName: doc.documentName || '',
						uploadDate: uploadDate ? dayjs(uploadDate).format('DD MMM, YYYY') : '',
						updatedBy: updatedByName,
						documentURL: resolvedBlobPath,
					};
				})
			);
		}
	}, [documentData]);

	useEffect(() => {
		if (documentError) setOpenError(true);
	}, [documentError]);

	useEffect(() => {
		if (saveData) {
			setOpenCreate(false);
			resetForm();
			getDocuments();
			setSuccessMessage('Document saved successfully!');
			setOpenSuccess(true);
		}
		if (saveError) setError('Something went wrong while saving document.');
	}, [saveData, saveError]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (deleteData) {
			setDeleteDialog(false);
			setSelectedRow(undefined);
			getDocuments();
			setSuccessMessage('Document deleted successfully!');
			setOpenSuccess(true);
		}
		if (deleteError) {
			setError('Something went wrong while deleting document.');
			setOpenError(true);
		}
	}, [deleteData, deleteError]); // eslint-disable-line react-hooks/exhaustive-deps

	const resetForm = () => {
		setDocumentType('');
		setOtherDocumentType('');
		setSelectedFile(undefined);
		setError('');
		if (inputRef.current) inputRef.current.value = '';
	};

	const handleDocumentTypeChange = (value: string) => {
		setDocumentType(value);
		if (String(value) !== '3') setOtherDocumentType('');
	};

	const handleOtherDocumentTypeChange = (value: string) => {
		setOtherDocumentType(value.slice(0, 161));
	};

	const handleDiscard = () => {
		resetForm();
		setOpenCreate(false);
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		let file = event.target.files?.[0];
		if (!file) return;

		const allowedExtensions = ['pdf', 'doc', 'docx', 'xlsx', 'xls'];
		const extension = file.name.split('.').pop()?.toLowerCase() || '';
		if (!allowedExtensions.includes(extension)) {
			setSelectedFile(undefined);
			setError('Allowed file types: .pdf, .doc, .docx, .xlsx, .xls');
			if (inputRef.current) inputRef.current.value = '';
			return;
		}

		setError('');
		setSelectedFile(file);
	};

	const handleSave = () => {
		const fileToUpload = selectedFile;
		const selectedDocType =
			documentTypeOptions.find((item) => item.key.toString() === String(documentType))?.value?.toString() || '';
		const isOtherDocumentType = selectedDocType.toLowerCase() === 'other';
		const resolvedDocType = isOtherDocumentType ? otherDocumentType.trim() : selectedDocType;
		let validationErrors = '';
		if (!selectedDocType) validationErrors += '|Document Type is required.';
		if (isOtherDocumentType && !resolvedDocType) validationErrors += '|Other Document Type is required.';
		if (!fileToUpload) validationErrors += '|File upload is required.';

		if (validationErrors) {
			setError(validationErrors);
			return;
		}

		if (!fileToUpload) return;

		const fileUpload = {
			id: '',
			documentType: resolvedDocType,
			documentName: fileToUpload.name,
			updatedBy: loggedInUser,
		};

		const formData = new FormData();
		formData.append('request', JSON.stringify(fileUpload));
		formData.append('file', fileToUpload);

		saveDocument(formData);
	};

	const handleDownload = (row: DocumentRowType) => {
		if (!row.documentURL) {
			setError('Document URL is not available for download.');
			setOpenError(true);
			return;
		}

		setActionMessage('Downloading document...');
		setActionBusy(true);
		downloadCUFile(row.documentURL, row.documentName)
			.then((res: any) => {
				fileDownload(res.data, row.documentName);
			})
			.catch(() => {
				setError('Something went wrong while downloading the document.');
				setOpenError(true);
			})
			.finally(() => {
				setActionBusy(false);
			});
	};

	const handlePreview = (row: DocumentRowType) => {
		if (!row.documentURL) {
			setError('Document URL is not available for preview.');
			setOpenError(true);
			return;
		}

		setActionMessage('Opening document...');
		setActionBusy(true);
		getCUBlobSasToken()
			.then((res: any) => {
				const token = res?.data || '';
				window.open(`${row.documentURL}${token}`, '_blank', 'noopener,noreferrer');
			})
			.catch(() => {
				setError('Something went wrong while opening the document.');
				setOpenError(true);
			})
			.finally(() => {
				setActionBusy(false);
			});
	};

	const handleDeleteClick = (row: DocumentRowType) => {
		if (!isAdmin) return;
		setSelectedRow(row);
		setDeleteDialog(true);
	};

	const handleDeleteConfirm = () => {
		if (!isAdmin) return;
		if (!selectedRow) return;
		if (!selectedRow.documentURL) {
			setError('Document URL is not available for delete.');
			setOpenError(true);
			return;
		}
		if (!selectedRow.id) {
			setError('Document id is not available for delete.');
			setOpenError(true);
			return;
		}
		deleteDocument(selectedRow.id, selectedRow.documentURL, selectedRow.documentName);
	};

	return (
		<>
			<Loader
				key="add-doc-loader"
				open={saveBusy || getDocumentsBusy || deleteBusy || actionBusy}
				message={
					actionBusy
						? actionMessage
						: saveBusy
						? 'Saving document...'
						: deleteBusy
						? 'Deleting document...'
						: 'Loading...'
				}
			/>
			{openError && (
				<CustomAlert
					open={openError}
					message={error || 'Something went wrong while retrieving documents!'}
					severity="error"
					timeout={5000}
					isFade={false}
					onCloseCallback={() => {
						setOpenError(false);
						setError('');
					}}
				/>
			)}
			{openSuccess && (
				<div
					className={`${classes.successToast} ${
						successMessage === 'Document deleted successfully!' ? classes.deleteToast : ''
					}`}
				>
					<CustomAlert
						open={openSuccess}
						message={successMessage}
						severity="success"
						timeout={5000}
						isFade
						onCloseCallback={() => {
							setOpenSuccess(false);
							setSuccessMessage('');
						}}
					/>
				</div>
			)}
			<CustomDialog
				open={deleteDialog}
				title="Delete Document"
				yesButtonText="Yes"
				noButtonText="No"
				onYesClickCallback={handleDeleteConfirm}
				onNoClickCallback={() => {
					setDeleteDialog(false);
					setSelectedRow(undefined);
				}}
				onCloseCallback={() => {
					setDeleteDialog(false);
					setSelectedRow(undefined);
				}}
				component={
					<div style={{ ...themes.bodyRegular }}>
						Are you sure you want to delete this document - {selectedRow?.documentName}?
					</div>
				}
			/>
			{openCreate && isAdmin ? (
				<AddCertificateDocument
					documentTypeOptions={documentTypeOptions}
					documentType={documentType}
					otherDocumentType={otherDocumentType}
					showOtherDocumentType={String(documentType) === '3'}
					selectedFile={selectedFile}
					error={error}
					inputRef={inputRef}
					onSelectDocumentType={handleDocumentTypeChange}
					onOtherDocumentTypeChange={handleOtherDocumentTypeChange}
					onFileChange={handleFileChange}
					onDiscard={handleDiscard}
					onSave={handleSave}
					onCloseError={() => setError('')}
				/>
			) : (
				<div className={classes.container}>
					{isAdmin && (
						<div className={classes.button}>
							<CustomButton
								id="add-document"
								text="Add Document"
								width={140}
								variant="contained"
								onClickCallback={() => setOpenCreate(true)}
							/>
						</div>
					)}
					<CertificateDocumentsTable
						rows={rows}
						allowDelete={isAdmin}
						onDownload={handleDownload}
						onPreview={handlePreview}
						onDelete={handleDeleteClick}
					/>
				</div>
			)}
		</>
	);
};

const useStyles = makeStyles(() => ({
	container: {
		paddingTop: '10px',
	},
	button: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginBottom: '6px',
		'& > div': {
			margin: '0 !important',
		},
		'& #add-document': {
			backgroundColor: `${color.primary[100]} !important`,
			color: `${color.text.light} !important`,
			paddingTop: '4px',
			paddingBottom: '4px',
		},
		'& #add-document:hover': {
			backgroundColor: `${color.primary[90]} !important`,
		},
	},
	successToast: {
		position: 'fixed',
		top: '16px',
		left: '50%',
		transform: 'translateX(-50%)',
		zIndex: 1400,
		minWidth: '320px',
		maxWidth: '420px',
	},
	deleteToast: {
		'& .MuiAlert-root': {
			backgroundColor: `${color.signal.warning30} !important`,
			color: `${color.text.dark} !important`,
		},
		'& .MuiAlert-icon, & .MuiAlert-action': {
			color: `${color.text.dark} !important`,
		},
	},
}));
